import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router';
import { useBrowseExperiences } from '../../hooks/api/useBrowseExperiences';
import { useTagGroups } from '../../hooks/api/useTagGroups';
import { useBrowseFilters } from '../../hooks/utils/useBrowseFilters';
import { AudioExperienceTile } from '../../components/audio-experience-tile';
import { VideoExperienceTile } from '../../components/video-experience-tile';
import { DropdownPill } from '../../components/dropdown-pill';
import { SearchInput } from '../../components/search-input';
import { Header } from '../../components/layout/header';
import { Footer } from '../../components/layout/footer';
import { ContentSection } from '../../components/content-section';
import { Experience, Content } from '../../hooks/api/useExperiences';
import { useMediaQuery } from '../../hooks/utils/useMediaQuery';
import { useAccumulateData } from '../../utils/pagination-accumulator';
import { TopicFilterDropdown } from '../../components/topic-filter-dropdown';
import { Features, useFeatureFlag } from '../../hooks/api/useFeatureFlag';
import { MatchStyleTrialDropdown } from '../../components/match-style-trial-dropdown';
import { useMatchStyleTrial } from '../../components/peer-listener-match-hint';

const EXPERIENCE_TYPE = {
  VIDEO: 'video',
  AUDIO: 'audio',
} as const;

const LABELS = {
  AUDIO: 'Audio',
  VIDEO: 'Video',
  VIDEO_STORIES: 'Video Stories',
  AUDIO_STORIES: 'Audio Stories',
} as const;

const BrowseExperiences = () => {
  const INITIAL_VIDEO_ITEMS = 12;
  const INITIAL_AUDIO_ITEMS = 8;
  const ITEMS_PER_PAGE = 50;

  const {
    selectedTagGroupId,
    setSelectedTagGroupId,
    selectedTagIds,
    selectedExperienceType,
    setSelectedExperienceType,
    searchQuery,
    setSearchQuery,
  } = useBrowseFilters(true, true);

  const matchPreviewEnabled = useFeatureFlag(Features.SEARCH_MATCH_EXPLAINABILITY).enabled;
  const { matchStyle, setMatchStyle } = useMatchStyleTrial(matchPreviewEnabled);
  const hasSearch = Boolean(searchQuery && searchQuery.trim().length > 0);
  const activeMatchStyle = hasSearch ? matchStyle : 'none';

  const { getTagGroupById } = useTagGroups(true);
  const { data: topicTagGroups } = useTagGroups();
  const [videoPage, setVideoPage] = useState(1);
  const [audioPage, setAudioPage] = useState(1);
  const [videoItemsShown, setVideoItemsShown] = useState(INITIAL_VIDEO_ITEMS);
  const [audioItemsShown, setAudioItemsShown] = useState(INITIAL_AUDIO_ITEMS);
  const [accumulatedVideoData, setAccumulatedVideoData] = useState<Content[]>([]);
  const [accumulatedAudioData, setAccumulatedAudioData] = useState<Content[]>([]);
  const location = useLocation();
  const querySignatureRef = useRef<string>('');
  const accumulatedAudioPagesRef = useRef<Set<number>>(new Set());
  const accumulatedVideoPagesRef = useRef<Set<number>>(new Set());

  const selectedTagGroup = useMemo(
    () => (selectedTagGroupId ? getTagGroupById(selectedTagGroupId) : undefined),
    [selectedTagGroupId, getTagGroupById],
  );

  const shouldFetchVideo = selectedExperienceType === undefined || selectedExperienceType === EXPERIENCE_TYPE.VIDEO;
  const shouldFetchAudio = selectedExperienceType === undefined || selectedExperienceType === EXPERIENCE_TYPE.AUDIO;

  const {
    data: videoData,
    isLoading: isLoadingVideo,
    error: videoError,
  } = useBrowseExperiences(
    selectedTagGroupId,
    undefined,
    selectedTagIds.length > 0 ? selectedTagIds : undefined,
    shouldFetchVideo ? EXPERIENCE_TYPE.VIDEO : undefined,
    searchQuery?.trim() || undefined,
    videoPage,
    ITEMS_PER_PAGE,
  );

  const {
    data: audioData,
    isLoading: isLoadingAudio,
    error: audioError,
  } = useBrowseExperiences(
    selectedTagGroupId,
    undefined,
    selectedTagIds.length > 0 ? selectedTagIds : undefined,
    shouldFetchAudio ? EXPERIENCE_TYPE.AUDIO : undefined,
    searchQuery?.trim() || undefined,
    audioPage,
    ITEMS_PER_PAGE,
  );

  const isLoadingExperiences = (shouldFetchVideo && isLoadingVideo) || (shouldFetchAudio && isLoadingAudio);
  const experiencesError = (shouldFetchVideo && videoError) || (shouldFetchAudio && audioError);

  const handleSearch = useCallback((query: string) => setSearchQuery?.(query), [setSearchQuery]);

  useEffect(() => {
    setVideoPage(1);
    setAudioPage(1);
    setVideoItemsShown(INITIAL_VIDEO_ITEMS);
    setAudioItemsShown(INITIAL_AUDIO_ITEMS);
    accumulatedVideoPagesRef.current = new Set();
    accumulatedAudioPagesRef.current = new Set();
    querySignatureRef.current = location.search;
  }, [selectedTagIds, selectedExperienceType, selectedTagGroupId, searchQuery, location.search]);

  // Accumulate paginated data using shared utility
  const accumulateData = useAccumulateData<Content>(querySignatureRef, location.search);

  useEffect(() => {
    accumulateData(
      videoData?.data,
      videoPage,
      videoItemsShown,
      setAccumulatedVideoData,
      setVideoItemsShown,
      INITIAL_VIDEO_ITEMS,
      accumulatedVideoPagesRef,
    );
  }, [videoData?.data, videoPage, accumulateData, videoItemsShown]);

  useEffect(() => {
    accumulateData(
      audioData?.data,
      audioPage,
      audioItemsShown,
      setAccumulatedAudioData,
      setAudioItemsShown,
      INITIAL_AUDIO_ITEMS,
      accumulatedAudioPagesRef,
    );
  }, [audioData?.data, audioPage, accumulateData, audioItemsShown]);

  /**
   * Returns filtered experiences, preventing stale data display during filter changes.
   */
  const getFilteredExperiences = useCallback(
    (accumulated: Content[], itemsShown: number, isLoading: boolean) => {
      const refMatches = querySignatureRef.current === location.search;
      if (!refMatches) {
        if (!isLoading && accumulated.length > 0) {
          querySignatureRef.current = location.search;
          return accumulated.slice(0, itemsShown);
        }
        return isLoading ? accumulated.slice(0, itemsShown) : [];
      }
      return accumulated.slice(0, itemsShown);
    },
    [location.search],
  );

  const videoExperiences = useMemo(
    () => getFilteredExperiences(accumulatedVideoData, videoItemsShown, isLoadingVideo),
    [accumulatedVideoData, videoItemsShown, isLoadingVideo, getFilteredExperiences],
  );

  const audioExperiences = useMemo(
    () => getFilteredExperiences(accumulatedAudioData, audioItemsShown, isLoadingAudio),
    [accumulatedAudioData, audioItemsShown, isLoadingAudio, getFilteredExperiences],
  );

  /**
   * Determines if next page should be prefetched based on remaining items.
   * Uses 20% of total loaded as threshold, with minimum of 2x initial items.
   */
  const shouldPrefetch = useCallback(
    (
      totalLoaded: number,
      itemsShown: number,
      isLoading: boolean,
      totalPages: number | undefined,
      currentPage: number,
      initialItems: number,
    ) => {
      if (totalLoaded === 0 || isLoading || !totalPages || currentPage >= totalPages) {
        return false;
      }
      const itemsRemaining = totalLoaded - itemsShown;
      const threshold = Math.max(Math.floor(totalLoaded * 0.2), initialItems * 2);
      return itemsRemaining <= threshold;
    },
    [],
  );

  /**
   * Prefetch effect: waits for state to sync before checking if next page should load.
   */
  const createPrefetchEffect = useCallback(
    (
      shouldFetch: boolean,
      isLoading: boolean,
      accumulatedData: Content[],
      itemsShown: number,
      totalPages: number | undefined,
      currentPage: number,
      initialItems: number,
      setPage: React.Dispatch<React.SetStateAction<number>>,
      accumulatedPagesRef: React.MutableRefObject<Set<number>>,
    ) => {
      if (!shouldFetch || isLoading) return;

      const expectedLength = accumulatedPagesRef.current.size * ITEMS_PER_PAGE;
      const lengthDiff = Math.abs(accumulatedData.length - expectedLength);
      if (lengthDiff > ITEMS_PER_PAGE / 2 && accumulatedPagesRef.current.size > 0) {
        return;
      }

      if (shouldPrefetch(accumulatedData.length, itemsShown, isLoading, totalPages, currentPage, initialItems)) {
        setPage((prev) => prev + 1);
      }
    },
    [shouldPrefetch],
  );

  useEffect(() => {
    createPrefetchEffect(
      shouldFetchVideo,
      isLoadingVideo,
      accumulatedVideoData,
      videoItemsShown,
      videoData?.total_pages,
      videoPage,
      INITIAL_VIDEO_ITEMS,
      setVideoPage,
      accumulatedVideoPagesRef,
    );
  }, [
    shouldFetchVideo,
    videoItemsShown,
    accumulatedVideoData,
    videoData?.total_pages,
    videoPage,
    isLoadingVideo,
    createPrefetchEffect,
  ]);

  useEffect(() => {
    createPrefetchEffect(
      shouldFetchAudio,
      isLoadingAudio,
      accumulatedAudioData,
      audioItemsShown,
      audioData?.total_pages,
      audioPage,
      INITIAL_AUDIO_ITEMS,
      setAudioPage,
      accumulatedAudioPagesRef,
    );
  }, [
    shouldFetchAudio,
    audioItemsShown,
    accumulatedAudioData,
    audioData?.total_pages,
    audioPage,
    isLoadingAudio,
    createPrefetchEffect,
  ]);

  /**
   * Handles "See More" click: shows more loaded items or fetches next page.
   */
  const createLoadMore = useCallback(
    (
      itemsShown: number,
      totalLoaded: number,
      totalPages: number | undefined,
      currentPage: number,
      setItemsShown: React.Dispatch<React.SetStateAction<number>>,
      setPage: React.Dispatch<React.SetStateAction<number>>,
      initialItems: number,
    ) => {
      if (itemsShown < totalLoaded) {
        setItemsShown((prev) => Math.min(prev + initialItems, totalLoaded));
      } else if (totalPages && currentPage < totalPages) {
        setPage((prev) => prev + 1);
      }
    },
    [],
  );

  const loadMoreVideos = useCallback(
    () =>
      createLoadMore(
        videoItemsShown,
        accumulatedVideoData.length,
        videoData?.total_pages,
        videoPage,
        setVideoItemsShown,
        setVideoPage,
        INITIAL_VIDEO_ITEMS,
      ),
    [accumulatedVideoData.length, videoData?.total_pages, videoPage, videoItemsShown, createLoadMore],
  );

  const loadMoreAudio = useCallback(
    () =>
      createLoadMore(
        audioItemsShown,
        accumulatedAudioData.length,
        audioData?.total_pages,
        audioPage,
        setAudioItemsShown,
        setAudioPage,
        INITIAL_AUDIO_ITEMS,
      ),
    [accumulatedAudioData.length, audioData?.total_pages, audioPage, audioItemsShown, createLoadMore],
  );

  const dfMdMedia = useMediaQuery('md');
  const showVideo = selectedExperienceType === EXPERIENCE_TYPE.VIDEO || selectedExperienceType === undefined;
  const showAudio = selectedExperienceType === EXPERIENCE_TYPE.AUDIO || selectedExperienceType === undefined;

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <Header />
      <div className={`flex-1 ${!dfMdMedia ? 'pt-[76px]' : ''}`}>
        <div className="max-w-page mx-auto px-6 py-8">
          <div className="mb-6">
            <h1 className="font-albert-sans text-30px mb-6 font-semibold text-black">Stories</h1>
            <div className={`mb-6 flex items-start justify-start gap-3 ${dfMdMedia ? 'flex-row' : 'flex-col'}`}>
              <SearchInput onSearch={handleSearch} initialValue={searchQuery || ''} className="mb-0" />
              <div className="flex flex-wrap items-start gap-3">
                <DropdownPill
                  label="All Stories"
                  placeholder="All Stories"
                  options={[
                    { value: EXPERIENCE_TYPE.AUDIO, label: LABELS.AUDIO_STORIES },
                    { value: EXPERIENCE_TYPE.VIDEO, label: LABELS.VIDEO_STORIES },
                  ]}
                  selectedValue={selectedExperienceType}
                  onSelect={(value) => setSelectedExperienceType(value as string | undefined)}
                />
                <TopicFilterDropdown
                  selectedTagGroupId={selectedTagGroupId}
                  onSelect={(value) => setSelectedTagGroupId(value as number | undefined)}
                  topicTagGroups={topicTagGroups}
                />
                {matchPreviewEnabled ? (
                  <MatchStyleTrialDropdown selectedValue={matchStyle} onSelect={setMatchStyle} />
                ) : null}
              </div>
            </div>
            {matchPreviewEnabled ? (
              <p className="font-open-sans mb-6 text-sm text-gray-500">
                Match styles are a preview for your client. Search to see why a story matched, then try a few
                treatments.
              </p>
            ) : null}
          </div>

          {experiencesError ? (
            <div className="text-center text-red-500">Error loading experiences. Please try again.</div>
          ) : (
            <div>
              <ContentSection
                title={LABELS.VIDEO_STORIES}
                items={videoExperiences}
                itemsShown={videoItemsShown}
                totalCount={videoData?.count}
                onLoadMore={loadMoreVideos}
                shouldShow={showVideo && (videoExperiences.length > 0 || isLoadingVideo)}
                isLoading={isLoadingVideo}
                renderItem={(experience, index) => (
                  <VideoExperienceTile
                    key={experience?.id || `skeleton-${index}`}
                    experience={experience as Experience | Content | undefined}
                    handleScheduleModalOpen={() => {}}
                    selectedTagGroupName={selectedTagGroup?.name}
                    isLoading={!experience}
                    matchStyle={activeMatchStyle}
                  />
                )}
              />

              <ContentSection
                title={LABELS.AUDIO_STORIES}
                items={audioExperiences}
                itemsShown={audioItemsShown}
                totalCount={audioData?.count}
                onLoadMore={loadMoreAudio}
                shouldShow={showAudio && (audioExperiences.length > 0 || isLoadingAudio)}
                isLoading={isLoadingAudio}
                renderItem={(experience, index) => (
                  <AudioExperienceTile
                    key={experience?.id || `skeleton-${index}`}
                    experience={experience as Experience | Content | undefined}
                    selectedTagGroupName={selectedTagGroup?.name}
                    isLoading={!experience}
                    matchStyle={activeMatchStyle}
                  />
                )}
              />

              {!isLoadingExperiences && (videoData?.count ?? 0) === 0 && (audioData?.count ?? 0) === 0 && (
                <div className="text-center text-gray-500">
                  <p className="text-lg">No experiences found.</p>
                  {(selectedTagGroupId || selectedTagIds.length > 0) && (
                    <p className="mt-2 text-sm">Try adjusting your filters.</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default BrowseExperiences;
